import type {
  PortfolioCalendar,
  PropertyCalendar,
  PropertyCalendarOrphanGapsLayer,
  PropertyCalendarSupply,
} from '@/api/client'
import { gapOpenValue } from '@/lib/gap-events'
import { supplyOpenValue } from '@/lib/supply-events'
import {
  holdOpenValue,
  ownerBlockOpenValue,
  parseBlockOpen,
} from '@/components/property/calendar/block-utils'
import { dayOpenValue, parseDayOpen } from '@/components/property/calendar/day-list'
import { cleaningOpenValue, stayOpenValue } from '@/components/property/calendar/sheet-open'
import { DEPOSIT_SECTION, REVIEW_SECTION } from '@/components/property/calendar/stay-sheet'
import type { PortfolioItem } from './item-actions'
import type { MergedLayer, PortfolioProperty } from './merge'
import { dayIso, dayNumber } from './windows'

/** A window as the sheets need it: its days, and its read when there is one. */
export interface SheetWindow {
  from: string
  to: string
  data: PortfolioCalendar | undefined
  /** When `data` was read: a later read is the fresher. */
  updatedAt: number
  isPending: boolean
}

type OkGapsLayer = Extract<PropertyCalendarOrphanGapsLayer, { status: 'ok' }>

function layerOf<T>(merged: MergedLayer<T>) {
  return merged.error !== null
    ? { status: 'error' as const, message: merged.error }
    : { status: 'ok' as const, items: merged.items }
}

/**
 * One property's calendar, in the property tab's own shape, from everything
 * the portfolio calendar has read for it (CAP-1418): what the shared sheets
 * look an item up in, so a sheet opened from the portfolio says exactly what it
 * would say on the property's Calendar tab, without a read of its own.
 *
 * The range is every window read, so a stay's turnover cleaning or a gap that
 * crosses a window edge is found wherever it falls. Each layer is the merged
 * one (`merge.ts`): one item per id, the latest read winning, and an error
 * when a window could not read it, which the sheets say as such. Two layers
 * need more than the merge keeps:
 * - supplies: the categories themselves (the merge keeps the events they put
 *   on days), one per category over every window, with every window's
 *   deliveries and the runout of the window that holds it;
 * - orphan gaps: when the stored scan ran and whether it is stale, from the
 *   latest read (every window reads the same snapshot), and whether each gap
 *   runs past the days read, which a window's own flags say only of itself.
 *
 * Undefined until a window has answered for the property.
 */
export function propertyCalendarOf(
  property: PortfolioProperty,
  windows: readonly SheetWindow[]
): PropertyCalendar | undefined {
  const rows = windows.flatMap(window => {
    const row = window.data?.properties.find(
      candidate => candidate.property_id === property.property_id
    )
    return window.data && row ? [{ window, data: window.data, row }] : []
  })
  if (rows.length === 0) return undefined
  const { layers } = property
  const latest = rows.reduce((best, read) =>
    read.window.updatedAt > best.window.updatedAt ? read : best
  )
  const latestGaps = rows.reduce<{ layer: OkGapsLayer; updatedAt: number } | null>(
    (best, { window, row }) => {
      const layer = row.layers.orphan_gaps
      return layer.status === 'ok' && (!best || window.updatedAt > best.updatedAt)
        ? { layer, updatedAt: window.updatedAt }
        : best
    },
    null
  )
  const from = rows[0].window.from
  const to = rows[rows.length - 1].window.to
  const orphanGaps: PropertyCalendarOrphanGapsLayer =
    layers.orphan_gaps.error === null && latestGaps
      ? {
          ...latestGaps.layer,
          items: layers.orphan_gaps.items.map(gap => ({
            ...gap,
            starts_before_range: gap.first_night < from,
            ends_after_range: gap.last_night > to,
          })),
        }
      : {
          status: 'error',
          code: 'unreadable',
          message: layers.orphan_gaps.error ?? 'Orphan gaps could not be read.',
          as_of: null,
        }

  return {
    property_id: property.property_id,
    time_zone: property.time_zone,
    from,
    to,
    generated_at: property.generated_at ?? latest.data.generated_at,
    layers: {
      stays: layerOf(layers.stays),
      blocks: layerOf(layers.blocks),
      cleaning: layerOf(layers.cleaning),
      deposits: layerOf(layers.deposits),
      reviews: layerOf(layers.reviews),
      supplies:
        layers.supplies.error !== null
          ? { status: 'error', message: layers.supplies.error }
          : {
              status: 'ok',
              items: mergeSupplies(
                [...rows]
                  .sort((a, b) => a.window.updatedAt - b.window.updatedAt)
                  .flatMap(({ row }) =>
                    row.layers.supplies.status === 'ok' ? row.layers.supplies.items : []
                  )
              ),
            },
      orphan_gaps: orphanGaps,
    },
  }
}

/**
 * One item per supply category across the windows' reads, oldest read first:
 * the latest read's facts (stock, confidence, the model's sentence), the
 * runout of a window that holds it, and every window's deliveries, in date
 * order. Each window lists only its own deliveries and counts the runout
 * only where it falls (`runout_in_range`), so no single window's item is the
 * whole answer.
 */
function mergeSupplies(items: PropertyCalendarSupply[]): PropertyCalendarSupply[] {
  const byCategory = new Map<string, PropertyCalendarSupply>()
  for (const item of items) {
    const kept = byCategory.get(item.category)
    if (!kept) {
      byCategory.set(item.category, item)
      continue
    }
    const runout = item.runout_in_range ? item : kept.runout_in_range ? kept : item
    // Each window lists only the deliveries in its own days, and windows do
    // not overlap: every entry is its own delivery, even two alike on a day.
    const deliveries = [...kept.deliveries, ...item.deliveries].sort((a, b) =>
      a.date.localeCompare(b.date)
    )
    byCategory.set(item.category, {
      ...item,
      runout_date: runout.runout_date,
      runout_in_range: runout.runout_in_range,
      deliveries,
    })
  }
  return [...byCategory.values()]
}

/**
 * Whether the sheet `open` names reads the property's blocks in full: a block's
 * panel or form, and a day's list (its blocks and their states).
 */
export function sheetReadsBlocks(open: string | null): boolean {
  return !!(parseBlockOpen(open) || parseDayOpen(open))
}

/** The day after `day` (`YYYY-MM-DD`): a range's exclusive end. */
export function nextDayIso(day: string): string {
  const number = dayNumber(day)
  return number === null ? day : dayIso(number + 1)
}

/**
 * Where a day stands on the portfolio calendar, for its day list: read when
 * the window holding it has answered, still being read while it is on its
 * way, and outside otherwise (a pasted link to days the grid is not showing,
 * or a window that could not be read).
 */
export function dayCoverage(
  windows: readonly SheetWindow[],
  day: string
): 'ok' | 'pending' | 'outside' {
  const window = windows.find(candidate => candidate.from <= day && day <= candidate.to)
  if (!window) return 'outside'
  if (window.data) return 'ok'
  return window.isPending ? 'pending' : 'outside'
}

/**
 * The sheet an item opens, as the property tab names it: a deposit or review
 * event opens its stay's sheet at that section, exactly as the tab's own
 * event blocks do, and "+N" opens the day's list.
 */
export function sheetFor(item: PortfolioItem): { value: string; section?: string } {
  switch (item.kind) {
    case 'stay':
      return { value: stayOpenValue(item.stay.reservation_id) }
    case 'block':
      return {
        value:
          item.block.kind === 'owner'
            ? ownerBlockOpenValue(item.block.id)
            : holdOpenValue(item.block.id),
      }
    case 'gap':
      return { value: gapOpenValue(item.gap.first_night) }
    case 'cleaning':
      return { value: cleaningOpenValue(item.job.id) }
    case 'deposit':
      return { value: stayOpenValue(item.event.reservationId), section: DEPOSIT_SECTION }
    case 'review':
      return { value: stayOpenValue(item.event.reservationId), section: REVIEW_SECTION }
    case 'supply':
      return { value: supplyOpenValue(item.supply.category, item.supply.day) }
    case 'more':
      return { value: dayOpenValue(dayIso(item.dayNumber)) }
  }
}
